import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Una sola pestana del Oper por navegador.
 *
 * El backend guarda un websocket por `user_id`, asi que abrir el Oper en una
 * segunda pestana desplazaba a la primera y la mandaba al login. Entre
 * pestanas del mismo navegador eso se puede evitar sin llegar al servidor:
 * la que llega pregunta por `BroadcastChannel` si ya hay una viva y, si la
 * hay, no abre socket. El relevo queda en manos del agente ("Usar aqui").
 *
 * Solo cubre el mismo origen y el mismo navegador. Otro equipo u otro
 * navegador siguen resolviendose en el backend, con el cierre por
 * SESSION_REPLACED_CLOSE_CODE.
 */

const CHANNEL_NAME = 'nebula-oper-tab-session'

// Cuanto espera la pestana nueva una respuesta antes de darse por unica. Es
// mensajeria local (mismo proceso del navegador): si en este plazo nadie
// contesta, no hay nadie.
const CLAIM_WAIT_MS = 400

type TabSessionStatus = 'idle' | 'active' | 'blocked'

type TabMessageType = 'WHO_IS_ACTIVE' | 'I_AM_ACTIVE' | 'TAKE_OVER' | 'RELEASED'

interface TabMessage {
  type: TabMessageType
  userId: string
  tabId: string
}

export const useTabSessionStore = defineStore('tab-session', () => {
  const status = ref<TabSessionStatus>('idle')

  // Identifica a esta pestana. Tambien desempata si dos se reclaman a la vez.
  const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`

  let channel: BroadcastChannel | null = null
  let currentUserId: string | null = null
  let pageHideBound = false

  const post = (type: TabMessageType) => {
    if (!channel || !currentUserId) return
    channel.postMessage({ type, userId: currentUserId, tabId } satisfies TabMessage)
  }

  const handleMessage = (event: MessageEvent<TabMessage>) => {
    const message = event.data
    // Mensajes de otra pestana y del mismo agente. Un `userId` distinto seria
    // otra cuenta en el mismo navegador: no se estorban.
    if (!message || message.tabId === tabId || message.userId !== currentUserId) return

    if (message.type === 'WHO_IS_ACTIVE' && status.value === 'active') {
      post('I_AM_ACTIVE')
      return
    }

    // Dos pestanas abiertas casi a la vez pueden reclamarse las dos antes de
    // oirse. Gana la de `tabId` menor: es un criterio estable que las dos
    // evaluan igual, sin necesidad de acordar nada.
    if (message.type === 'I_AM_ACTIVE' && status.value === 'active') {
      if (tabId > message.tabId) status.value = 'blocked'
      return
    }

    if (message.type === 'TAKE_OVER' && status.value === 'active') {
      status.value = 'blocked'
      return
    }

    // La pestana activa se cerro: la que estaba esperando ocupa su lugar sin
    // que el agente tenga que hacer nada.
    if (message.type === 'RELEASED' && status.value === 'blocked') {
      status.value = 'active'
    }
  }

  const bindPageHide = () => {
    if (pageHideBound) return
    window.addEventListener('pagehide', handlePageHide)
    pageHideBound = true
  }

  const handlePageHide = () => {
    if (status.value === 'active') post('RELEASED')
  }

  const ensureChannel = () => {
    if (channel) return
    channel = new BroadcastChannel(CHANNEL_NAME)
    channel.addEventListener('message', handleMessage as EventListener)
    bindPageHide()
  }

  /** Pregunta si hay otra pestana activa para este agente. */
  const askWhoIsActive = (userId: string): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      const listener = (event: MessageEvent<TabMessage>) => {
        const message = event.data
        if (!message || message.tabId === tabId || message.userId !== userId) return
        if (message.type === 'I_AM_ACTIVE') resolve(true)
      }

      channel?.addEventListener('message', listener as EventListener)
      post('WHO_IS_ACTIVE')

      window.setTimeout(() => {
        channel?.removeEventListener('message', listener as EventListener)
        resolve(false)
      }, CLAIM_WAIT_MS)
    })

  /**
   * Reclama el puesto de pestana activa. Devuelve si lo consiguio.
   *
   * Sin soporte de `BroadcastChannel` se concede siempre: quedarse sin Oper
   * es peor que arriesgar el desplazamiento, que el backend ya resuelve.
   */
  const claim = async (userId: string): Promise<boolean> => {
    currentUserId = userId

    if (typeof BroadcastChannel === 'undefined') {
      status.value = 'active'
      return true
    }

    ensureChannel()

    const answered = await askWhoIsActive(userId)

    status.value = answered ? 'blocked' : 'active'
    if (status.value === 'active') post('I_AM_ACTIVE')

    return !answered
  }

  /**
   * El backend cerro nuestro websocket por reemplazo: .fue una hermana?
   *
   * Si contesta otra pestana de este navegador, la sesion del agente sigue
   * viva —es la misma cuenta y el mismo token— y lo unico que perdimos es el
   * puesto. Cerrar la sesion ahi seria destructivo: `localStorage` es
   * compartido, asi que nos llevariamos por delante a la pestana que si esta
   * trabajando. Devuelve si cedimos el puesto a una hermana.
   */
  const yieldIfSiblingActive = async (): Promise<boolean> => {
    if (!channel || !currentUserId) return false

    const sibling = await askWhoIsActive(currentUserId)
    if (sibling) status.value = 'blocked'

    return sibling
  }

  /** Relevo explicito: el agente decide trabajar en esta pestana. */
  const takeOver = () => {
    post('TAKE_OVER')
    status.value = 'active'
  }

  /** Logout o cierre: deja el puesto libre para una pestana que espere. */
  const release = () => {
    if (status.value === 'active') post('RELEASED')
    status.value = 'idle'
    currentUserId = null
  }

  return {
    status,
    claim,
    yieldIfSiblingActive,
    takeOver,
    release,
  }
})
